import { serialize, deserialize, plainToClass, Type, Exclude } from "class-transformer";
import { Threshold, Project } from "./project";

export class Paper {
  id: number;
  CEPI_name: string;
  description: string;
  name: string;
  deleted: boolean;
  newest: Paper;
  placeholder: string = null;
}

class Flute {
  id: number;
  name: string;
  deleted: boolean;
  newest: Flute;
  placeholder: string = null;
}

export enum LayerType {
  PAPER,
  FLUTING
}

export class Layer {
  type: string;
  paper: Paper = null;
  flute: Flute = null;
}

export class ThresholdResult {
  RH_value: string;
  weeks: number;
}

export class Board {
  id: number;
  index: number;
  name: string = '';
  description: string = '';
  visible: boolean = true;
  use_custom_bpi: boolean = false;
  custom_bpi: number = null;
  cached_default_bpi: number = null;

  cssClass: string;
  cssClassDark: string;
  cssClassLight: string;

  enableSimulation: boolean = false;
  activeSimulation: boolean = false;
  simulationProgress: number = 0;
  simulationResults: any = [];
  thresholdResults: ThresholdResult[] = [];

  layers: Layer[] = [];

  board_result: any = null;

  hasBoardResults()
  {
    return this.simulationResults.length != 0;
  }

  hasSimulationResults()
  {
    return this.simulationResults.length != 0 && this.simulationResults[0].status != null;
  }

  hasSimulationSuccess()
  {
    return this.simulationResults.length != 0 && this.simulationResults[0].status == 0;
  }

  hasSimulationSuccessWithHumidityImpact()
  {
    return this.simulationResults.length != 0 && this.simulationResults[0].status == 0 && this.simulationResults[0].stacking_factors_result != null;
  }

  hasSimulationSuccessWithCarbonFootprint()
  {
    return this.simulationResults.length != 0 && this.simulationResults[0].status == 0 && this.simulationResults[0].carbon_footprint != null;
  }

  hasSimulationSuccessWithFoodSafety()
  {
    return this.simulationResults.length != 0 && this.simulationResults[0].status == 0 && this.simulationResults[0].food_contact_risk != null;
  }

  hasCachedDefaultBPI()
  {
    return this.cached_default_bpi != null;
  }

  hasSimulationErrors()
  {
    return this.hasSimulationResults() && this.simulationResults[0].status != 0;
  }

  hasBoardResultsWithoutErrors()
  {
    return this.hasBoardResults() && !this.hasSimulationErrors();
  }

  getCssClassFromSimulationResult(isBCT = false)
  {
    if (this.hasSimulationErrors())
      return "error";
    else if (!this.hasBoardResults() || isBCT)
      return this.cssClass;
    else
      return "";
  }

  getTextColorFromSimulationResult()
  {
    if (this.hasSimulationErrors())
      return "white";
    else if (!this.hasBoardResults())
      return "white";
    else
      return "grey";
  }

  setSimulationResult(result, project: Project)
  {
    // Present weight as g/m^2
    result.grammage = result.grammage * 1000.0;

    // Present thickness as mm
    result.thickness = result.thickness * 1000.0;

    // Present ect as kN/m
    result.ect = result.ect / 1000.0;

    // Present burst as kPa
    result.burst = result.burst / 1000.0;

    // Assume this.simulationResults is an empty array
    this.simulationResults.push(result);

    this.cached_default_bpi = result.default_bpi;

    if (this.hasSimulationSuccessWithHumidityImpact())
      this.setThresholdResults(project.thresholds);
  }

  setThresholdResults(thresholds: Threshold[])
  {
    const result = this.simulationResults[0];
    // Assume this.thresholdResults is an empty array
    for (let threshold of thresholds) {
      let weeks = Infinity;
      let x = result.stacking_factors_result.weeks;
      let RH_result = result.stacking_factors_result.RH_results.find( r => r.RH_value == threshold.RH_value);
      let y = RH_result.bct_values;
      let bct_threshold = threshold.bct_value;

      if (bct_threshold > y[0]) {
        weeks = 0;
      } else {
        for (let i = 0; i + 1 < y.length; i++) {
          const y0 = y[i];
          const x0 = x[i];
          const y1 = y[i + 1];
          const x1 = x[i + 1];

          // Interpolate the intersection between the threshold and the bct values
          if (bct_threshold <= y0 && bct_threshold >= y1) {
            let m = (y1 - y0) / (x1 - x0);
            let b = y0 - m * x0;
            weeks = (bct_threshold - b) / m;
          }
        }
      }

      this.thresholdResults.push({ RH_value: threshold.RH_value, weeks: weeks });
    }
  }

  numLayersText() {
    var numLayers = this.layers.length;
    var text = 'NO_BOARD'
    if ( numLayers == 3 ) {
      text = 'Single Wall Board';
    }
    else if (numLayers == 5)
    {
      text = 'Double Wall Board';
    }
    else
    {
      text = 'Tripple Wall Board';
    }
    return text;
  }

  updateEnableSimulation() {
    this.enableSimulation = true;

    for (let layer of this.layers) {
      if (layer.paper == null || layer.paper.deleted || layer.paper.newest || layer.paper.placeholder) {
        this.enableSimulation = false;
      }

      if (layer.type === LayerType[LayerType.FLUTING]) {
        if (layer.flute == null || layer.flute.deleted || layer.flute.newest || layer.flute.placeholder) {
          this.enableSimulation = false;
        }
      }
    }
  }

  asDatabaseBoard(): any {
    let board = JSON.parse(JSON.stringify(this));

    delete board.activeSimulation;
    delete board.simulationProgress;
    delete board.simulationResults;
    delete board.thresholdResults;
    delete board.enableSimulation;

    for (let layer of board.layers) {
      delete layer.id;
      delete layer.__typename;

      if (layer.paper)
        layer.paper = { id: layer.paper.newest ? layer.paper.newest.id : layer.paper.id };

      if (layer.flute)
        layer.flute = { id: layer.flute.newest ? layer.flute.newest.id : layer.flute.id };
    }

    return board;
  }

  asResponseBoard(): any {
    let board = JSON.parse(JSON.stringify(this));

    board.id = -1;
    board.__typename = 'Board';

    for (let layer of board.layers) {
      if (layer.type === LayerType[LayerType.PAPER])
        layer.id = -1;
      else
        layer.id = -2;

      layer.__typename = 'Layer';
    }

    return board;
  }

  store():any {
    return serialize(this);
  }

  static restore(eBoard: any) {
    return plainToClass<Board, Object>(Board, eBoard);
  }
}



// WEBPACK FOOTER //
// ../common/board.ts